import { Box } from '@mui/material';
import { Formik } from 'formik';
import { useMemo, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomStepper from 'components/shared/CustomStepper/CustomStepper';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { DealsDetailsType } from 'model/deals.model';

import { modalReducer } from 'reducers/modalReducer';
import RoutePaths from 'routes/RoutePaths';
import { DealsService } from 'service/deals.service';
import { useAppSelector } from 'store/hooks';
import CancelModal from './CancelModal';
import { METADATA_FIELDS, steps } from './data';
import { coreDealInformation } from './fields.config';
import { muiStyles } from './muiStyles';

type PresetClient = {
  id?: string;
  name?: string;
};

export const DealForm = ({
  isEdit,
  deal,
  presetClient,
  lockClient = false,
}: {
  isEdit: boolean;
  deal?: any;
  presetClient?: PresetClient;
  lockClient?: boolean;
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);

  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [modalState, dispatch] = useReducer(modalReducer, {
    cancel: false,
  });

  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const initialValues = useMemo<DealsDetailsType>(() => {
    const services =
      deal?.services && deal.services.length > 0
        ? deal.services
        : [{ serviceId: '', targetPrice: 0 }];

    if (isEdit) {
      return {
        ...deal,
        services,
      };
    }

    return {
      services: [{ serviceId: '', targetPrice: 0 }],
      owner: user,
      lockClient,
      client:
        presetClient?.id || presetClient?.name
          ? {
              id: presetClient?.id,
              accountId: presetClient?.id,
              name: presetClient?.name,
              clientName: presetClient?.name,
            }
          : undefined,
    };
  }, [isEdit, deal, lockClient, presetClient?.id, presetClient?.name, user]);

  const handleDealSubmit = async (values: DealsDetailsType) => {
    const organizationId = sessionStorage.getItem('organizationId') || undefined;
    const { expectCloseDate, ...restDealDetails } = values;

    const updatableFields = { ...restDealDetails };

    METADATA_FIELDS.forEach((field) => {
      delete updatableFields[field as keyof typeof updatableFields];
    });

    Object.keys(updatableFields).forEach((key) => {
      const value = updatableFields[key as keyof typeof updatableFields];
      if (value === '') {
        delete updatableFields[key as keyof typeof updatableFields];
      }
    });

    if (values.services.length === 1 && values.services[0].serviceName === '') {
      delete updatableFields['services'];
    }

    const payload = {
      ...updatableFields,
      organizationId,
      client: ((obj: any) => {
        return {
          accountId: obj.id || obj.accountId,
          clientName: obj.name || obj.clientName,
          avatar: obj.avatar ?? null,
        };
      })(restDealDetails.client),
      business: ((obj: any) => {
        return {
          businessId: obj.id || obj.businessId,
          name: obj.name || '',
        };
      })(restDealDetails.business),
      owner: ((obj: any) => {
        const { id, name, createdAt, createdBy, ...rest } = obj;

        return rest;
      })(restDealDetails.owner),
      closeDate: restDealDetails.closeDate || expectCloseDate || undefined,
    };

    const operationType = isEdit && id ? 'update' : 'create';

    try {
      startOperation(operationType, isEdit && id ? 'Updating deal...' : 'Creating deal...');

      let response;
      if (isEdit && id) {
        try {
          response = await DealsService.updateDeal(id, payload);
        } catch (dealsError) {}
      } else {
        response = await DealsService.createDeal(payload);
      }

      completeOperation(operationType, `Deal ${isEdit ? 'updated' : 'created'} successfully`);
      navigate(`${RoutePaths.Deals}/${response.dealId}`);
    } catch (err: any) {
      completeOperation(
        operationType,
        err?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} deal`,
        true,
      );
    }
  };

  const onCancelEdit = () => dispatch({ type: 'OPEN', payload: 'cancelEdit' });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values) => {
        await handleDealSubmit(values);
      }}
    >
      {({ values, handleSubmit }) => {
        const CurrentStep = steps[activeStep];

        const isStep0Valid = () => {
          const requiredFields = coreDealInformation
            .filter((item) => item.required)
            .map((item) => item.name);

          const service = values?.services?.[0];
          const isServiceValid =
            service?.serviceId !== '' && service?.targetPrice !== 0 && service?.targetPrice !== '';

          return (
            isServiceValid &&
            requiredFields.every((fieldName) => {
              const value = values[fieldName];
              return value !== undefined && value !== null && value !== '';
            })
          );
        };

        return (
          <>
            <Box sx={muiStyles.formRoot}>
              <Box sx={muiStyles.stepsHeader}>
                <CustomStepper
                  activeStep={activeStep}
                  steps={steps.map((s) => s.label)}
                  setActiveStep={setActiveStep}
                  setCompleted={setCompleted}
                  completed={completed}
                  disabled={activeStep === 0 || false}
                  style={{ width: '50%' }}
                />
              </Box>

              <Box sx={muiStyles.stepContent}>
                <CurrentStep.Content />
              </Box>

              <Box sx={muiStyles.controls}>
                <CustomButton
                  text={'Cancel'}
                  onClick={onCancelEdit}
                  variant='outlined'
                  style={muiStyles.cancelButton}
                  disabled={false}
                />

                <Box sx={muiStyles.navigationControls}>
                  {activeStep !== 0 && (
                    <CustomButton
                      text={'Previous'}
                      onClick={() => setActiveStep((currentStep) => currentStep - 1)}
                      variant='outlined'
                      style={muiStyles.previousButton}
                      disabled={activeStep === 0}
                    />
                  )}

                  <CustomButton
                    text={activeStep === 0 ? 'Continue' : 'Save'}
                    onClick={
                      activeStep === 0
                        ? () => setActiveStep((currentStep) => currentStep + 1)
                        : handleSubmit
                    }
                    style={muiStyles.actionButton}
                    variant={'contained'}
                    disabled={!isStep0Valid()}
                  />
                </Box>
              </Box>
            </Box>

            <CustomAlert
              open={alertState.status !== 'idle'}
              handleClose={() => setAlertState(initialAlertState)}
              message={getAlertContent(alertState)}
              error={alertState.status === 'error'}
            />

            <CancelModal state={modalState.cancelEdit} dispatch={dispatch} />
          </>
        );
      }}
    </Formik>
  );
};
